import {
  getInAppBrowserApp,
  getOpenInBrowserHint,
} from "@/lib/utils/browser-utils";
import { Text } from "@radix-ui/themes";
import { HiOutlineExclamationCircle } from "react-icons/hi";

/**
 * Warns, before the tap rather than after it, that this browser cannot save the
 * download. See `getInAppBrowserApp` for why the file silently goes nowhere in
 * Instagram, Facebook and X on iOS, and why nothing on our side can fix it.
 *
 * Renders nothing in a browser that downloads normally, so it is safe to place
 * next to any download control unconditionally.
 */
export default function InAppBrowserDownloadNotice({
  tone = "dark",
  subject = "files",
  className = "",
}: {
  /** `dark` for the album page's artwork backdrop, `light` for the account pages. */
  tone?: "dark" | "light";
  /**
   * What the nearby control downloads, as it reads after "To save your ...".
   * Defaults to the generic "files" because the purchases list mixes album
   * ZIPs, track assets and license PDFs; pass "album" where that is all the
   * page offers.
   */
  subject?: string;
  className?: string;
}) {
  const app = getInAppBrowserApp();
  if (!app) return null;

  const toneClasses =
    tone === "dark"
      ? "bg-white/[0.07] border-yellow-500/25 text-yellow-300"
      : "bg-yellow-100 border-yellow-600/40 text-yellow-900";

  return (
    <div
      data-testid="in-app-browser-download-notice"
      className={`flex items-start gap-2 rounded-[8px] border px-3 py-2 ${toneClasses} ${className}`}
    >
      <HiOutlineExclamationCircle
        className="w-4 h-4 mt-[1px] shrink-0"
        aria-hidden="true"
      />
      <Text className="text-[12px] font-normal leading-[140%]">
        Downloads don&apos;t work inside {app}. To save your {subject},{" "}
        {getOpenInBrowserHint(app)}, then tap Download again.
      </Text>
    </div>
  );
}
